import { supabase } from '@/integrations/supabase/client';

export interface Scrape {
  id: string;
  uuid: string;
  slug: string;
  company_slug: string;
  version: string;
  screenshot_url: string | null;
  cloudinary_url: string | null;
  markdown_url: string | null;
  html_url: string | null;
  timestamp: string;
  status: string;
  last_error: string | null;
  url: string | null; // Original URL from pages table
  verified: boolean | null;
  verification_details: any | null;
  scraper?: string | null;
  pricing_page_url?: string | null;
  no_pricing?: boolean | null;
  page_id?: string | null;
}

export interface Company {
  slug: string;
  category?: string;
}

/**
 * Fetch all unique scrapes (latest by slug) from scrapes_data view
 */
export const fetchScrapes = async (): Promise<Scrape[]> => {
  console.log(`🔄 [scrapeDatabase] Fetching all unique scrapes from scrapes_data view`);

  try {
    // Get all scrapes from the scrapes_data view which has everything joined
    const { data: allScrapes, error: fetchError } = await (supabase as any)
      .from('scrapes_data')
      .select('*')
      .order('timestamp', { ascending: false });

    if (fetchError) {
      throw fetchError;
    }

    console.log(`🔍 [scrapeDatabase] Fetched ${allScrapes?.length || 0} scrapes from scrapes_data view`);

    // Create a map to track the latest scrape for each slug
    const latestScrapeMap = new Map();
    
    allScrapes.forEach((scrape: any) => {
      if (!latestScrapeMap.has(scrape.slug)) {
        // Map the scrapes_data fields to match our Scrape interface
        const mappedScrape = {
          ...scrape,
          url: scrape.pricing_page_url || scrape.url || 'N/A', // Use pricing_page_url as the main URL
          uuid: scrape.uuid || `${scrape.slug}-${scrape.version}`, // Generate uuid if missing
          verification_details: scrape.verification_details || null
        };
        
        latestScrapeMap.set(scrape.slug, mappedScrape);
      }
    });

    // Convert the map to an array and sort by timestamp
    const uniqueScrapes = Array.from(latestScrapeMap.values());
    uniqueScrapes.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    console.log(`✅ [scrapeDatabase] Fetched ${uniqueScrapes.length} unique scrapes from scrapes_data view`);
    return uniqueScrapes;
  } catch (error) {
    console.error('❌ [scrapeDatabase] Error fetching scrapes from scrapes_data view:', error);
    throw error;
  }
};

/**
 * Fetch companies for dropdown selection
 */
export const fetchCompanies = async (): Promise<Company[]> => {
  console.log('🔄 [scrapeDatabase] Fetching companies for dropdown');

  const { data, error } = await supabase
    .from('companies')
    .select('slug, category')
    .order('slug', { ascending: true });

  if (error) {
    throw error;
  }

  console.log(`✅ [scrapeDatabase] Fetched ${data.length} companies`);
  return data;
};

/**
 * Extract unique categories from companies data
 */
export const extractCategories = (companiesData: Company[]): string[] => {
  console.log('🔄 [scrapeDatabase] Extracting unique categories');
  
  // Extract unique categories
  const uniqueCategories = Array.from(new Set(
    companiesData
      .map(company => company.category)
      .filter(Boolean) // Remove null/undefined values
  )) as string[];
  
  // Sort categories alphabetically
  uniqueCategories.sort();
  
  console.log(`✅ [scrapeDatabase] Found ${uniqueCategories.length} unique categories`);
  return uniqueCategories;
};

/**
 * Update pricing page URL for a company's page
 */
export const updatePricingPageUrl = async (scrape: Scrape, newUrl: string): Promise<void> => {
  // Use page_data view to find the page by slug, then update the pages table
  console.log(`🔍 [updatePricingPageUrl] Looking up slug: ${scrape.slug}`);
  
  const { data: pageData, error: pageError } = await (supabase as any)
    .from('page_data')
    .select('id, pricing_page_url')
    .eq('slug', scrape.slug)
    .single();
  
  console.log(`🔍 [updatePricingPageUrl] Page lookup result:`, { pageData, pageError });
  
  if (pageError || !pageData) {
    throw new Error(`No page found for slug: ${scrape.slug}`);
  }
  
  const pageId = pageData.id;
  console.log(`🔍 [updatePricingPageUrl] Found page ID: ${pageId}, current URL: ${pageData.pricing_page_url}`);
  console.log(`🔍 [updatePricingPageUrl] Updating to new URL: ${newUrl}`);

  // Update the pages table using page ID
  const { data: updateData, error: updateError } = await supabase
    .from('pages')
    .update({ pricing_page_url: newUrl })
    .eq('id', pageId)
    .select();
  
  console.log(`🔍 [updatePricingPageUrl] Update result:`, { updateData, updateError });

  if (updateError) {
    throw updateError;
  }
};

/**
 * Update company scraper method
 */
export const updateCompanyScraper = async (slug: string, newScraper: string): Promise<void> => {
  const { error } = await supabase
    .from('companies')
    .update({ scraper: newScraper } as any) // Use 'as any' to bypass type checking for scraper field
    .eq('slug', slug);

  if (error) {
    throw error;
  }
};

/**
 * Bulk update scraper method for multiple companies
 */
export const bulkUpdateScraper = async (slugs: string[], newScraper: string): Promise<{ successCount: number; failureCount: number }> => {
  console.log(`🔄 [scrapeDatabase] Bulk updating scraper to ${newScraper} for ${slugs.length} companies`);
  
  let successCount = 0;
  let failureCount = 0;
  
  // Process each company individually to track success/failure
  for (const slug of slugs) {
    try {
      await updateCompanyScraper(slug, newScraper);
      successCount++;
    } catch (error) {
      console.error(`Failed to update scraper for ${slug}:`, error);
      failureCount++;
    }
  }
  
  console.log(`✅ [scrapeDatabase] Bulk scraper update completed: ${successCount} success, ${failureCount} failed`);
  return { successCount, failureCount };
};

/**
 * Update the no_pricing flag for a scrape
 */
export const updateNoPricingFlag = async (scrape: Scrape, noPricing: boolean): Promise<void> => {
  if (!scrape.page_id) {
    throw new Error(`No page_id found for scrape: ${scrape.slug}`);
  }
  
  console.log(`🔍 [updateNoPricingFlag] Updating page_id: ${scrape.page_id} to no_pricing: ${noPricing}`);

  // Update the pages table using page_id directly from scrapes_data view
  const { error: updateError } = await supabase
    .from('pages')
    .update({ no_pricing: noPricing } as any)
    .eq('id', scrape.page_id);
  
  if (updateError) {
    console.error(`❌ [updateNoPricingFlag] Update error:`, updateError);
    throw updateError;
  }
  
  console.log(`✅ [updateNoPricingFlag] Successfully updated page ${scrape.page_id} no_pricing to ${noPricing}`);
};

/**
 * Update the verified status for a scrape
 */
export const updateScrapeVerified = async (scrape: Scrape, verified: boolean): Promise<void> => {
  console.log(`🔍 [updateScrapeVerified] Updating scrape ${scrape.slug} to verified: ${verified}`);

  // Update the scrapes table directly
  const { error: updateError } = await supabase
    .from('scrapes')
    .update({ verified: verified })
    .eq('slug', scrape.slug)
    .eq('version', scrape.version);
  
  if (updateError) {
    console.error(`❌ [updateScrapeVerified] Update error:`, updateError);
    throw updateError;
  }
  
  console.log(`✅ [updateScrapeVerified] Successfully updated scrape ${scrape.slug}/${scrape.version} verified to ${verified}`);
};

/**
 * Get a scrape by slug and version
 */
export const getScrapeBySlugAndVersion = async (slug: string, version: string): Promise<Scrape> => {
  console.log(`🔄 [scrapeDatabase] Getting scrape for ${slug}, version: ${version}`);
  
  const { data, error } = await supabase
    .from('scrapes')
    .select('*')
    .eq('slug', slug)
    .eq('version', version)
    .single();
  
  if (error) {
    throw error;
  }
  
  // Add missing properties to match Scrape interface
  const scrapeData = data as any;
  return {
    ...scrapeData,
    uuid: scrapeData.uuid || scrapeData.id, // Use id as uuid if uuid is missing
    verification_details: scrapeData.verification_details || null,
    scraper: null, // This will be populated by fetchScrapes if needed
    pricing_page_url: null // This will be populated by fetchScrapes if needed
  } as Scrape;
};
